// @ts-nocheck
import React, { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { useMentionNotifications } from "@/hooks/useMentionNotifications";
import { useAthleteMentions } from "@/hooks/useAthleteMentions";
import { useParentAccessRequests } from "@/hooks/useParentAccessRequests";
import { useAuth } from "@/contexts/AuthContext";
import MentionApprovalModal from "@/components/press/MentionApprovalModal";
import VideoMentionModal from "@/components/press/VideoMentionModal";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  related_module: string;
  related_id: string;
  read: boolean;
  created_at: string;
}

interface Article {
  id: string;
  title: string;
  content: string;
  author_name?: string;
}

interface Video {
  id: string;
  title: string;
  description?: string;
  author_name?: string;
}

// Updated to match article_mentions schema
interface ArticleMention {
  id: string;
  mentioned_profile_id: string;
  article_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
}

interface VideoMention {
  id: string;
  mentioned_profile_id: string;
  source_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
}

const NotificationDropdown: React.FC = () => {
  const { profile } = useAuth();
  const { notifications, unreadCount, markAsRead, fetchNotifications } = useMentionNotifications();
  const { updateMentionStatus } = useAthleteMentions();
  const { requests: parentRequests, respondToRequest } = useParentAccessRequests();
  const [selectedMention, setSelectedMention] = useState<ArticleMention | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [selectedVideoMention, setSelectedVideoMention] = useState<VideoMention | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Calculate total unread count including parent requests
  const totalUnreadCount = unreadCount + (profile?.role === 'athlete' ? parentRequests.length : 0);

  const handleParentRequestResponse = async (requestId: string, response: 'approved' | 'rejected') => {
    const success = await respondToRequest(requestId, response);
    if (success) {
      const action = response === 'approved' ? 'approved' : 'rejected';
      toast.success(`Parent access request ${action}`);
    } else {
      toast.error('Failed to respond to request');
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    console.log('🔔 Notification clicked:', notification);
    
    // Mark as read
    await markAsRead(notification.id);

    // Handle video mentions differently from article mentions
    if (notification.type === 'mention_request' && notification.related_module === 'profile_mentions' && notification.related_id) {
      try {
        setLoading(true);
        console.log('🔍 Fetching mention details for mention ID:', notification.related_id);

        // Fetch the profile mention to determine type
        const { data: mention, error: mentionError } = await supabase
          .from('profile_mentions')
          .select('*')
          .eq('id', notification.related_id)
          .single();

        if (mentionError || !mention) {
          console.error('❌ Error fetching mention:', mentionError);
          toast.error('Failed to load mention details');
          return;
        }

        // Handle video mentions
        if (mention.source_type === 'video') {
          console.log('🎥 Handling video mention');
          
          // Fetch video details
          const { data: video, error: videoError } = await supabase
            .from('videos')
            .select('id, title, description')
            .eq('id', mention.source_id)
            .single();

          if (videoError) {
            console.error('❌ Error fetching video:', videoError);
            toast.error('Video not found or has been deleted');
            return;
          }

          // Get author name
          let authorName = 'Admin';
          if (mention.mentioned_by) {
            const { data: authorProfile } = await supabase
              .from('profiles')
              .select('name')
              .eq('id', mention.mentioned_by)
              .single();
            
            authorName = authorProfile?.name || 'Admin';
          }

          setSelectedVideoMention({
            id: mention.id,
            mentioned_profile_id: mention.profile_id,
            source_id: mention.source_id,
            status: mention.status,
            created_at: mention.created_at,
            approved_at: mention.approved_at,
            rejected_at: mention.rejected_at
          });

          setSelectedVideo({
            id: video.id,
            title: video.title || 'Untitled Video',
            description: video.description,
            author_name: authorName
          });

          setVideoModalOpen(true);
          return;
        }
      } catch (error) {
        console.error('❌ Unexpected error fetching mention details:', error);
        toast.error('Failed to load mention details');
      } finally {
        setLoading(false);
      }
    }

    // Handle article mentions (legacy support)
    if (notification.type === 'mention_request' && notification.related_module === 'press' && notification.related_id) {
      try {
        setLoading(true);
        console.log('🔍 Fetching mention and article details for mention ID:', notification.related_id);

        // Step 1: Fetch the article mention safely
        const { data: mention, error: mentionError } = await supabase
          .from('article_mentions')
          .select('*')
          .eq('id', notification.related_id)
          .single();

        if (mentionError) {
          console.error('❌ Error fetching mention:', mentionError);
          toast.error('Failed to load mention details');
          return;
        }

        if (!mention) {
          console.error('❌ No mention found with ID:', notification.related_id);
          toast.error('Mention not found');
          return;
        }

        console.log('✅ Fetched mention data:', mention);

        // Article data no longer available (press_articles removed)
        let articleData = null;
        let authorName = 'Unknown Author';

        // Set the mention data for the modal
        setSelectedMention({
          id: mention.id,
          mentioned_profile_id: mention.mentioned_profile_id,
          article_id: mention.article_id,
          status: mention.status,
          created_at: mention.created_at,
          approved_at: mention.approved_at,
          rejected_at: mention.rejected_at
        });

        if (articleData) {
          setSelectedArticle({
            id: articleData.id,
            title: articleData.title || 'Untitled Article',
            content: articleData.content || 'No content available',
            author_name: authorName
          });
        }

        setModalOpen(true);
        console.log('✅ Modal should now open with mention and article data');
      } catch (error) {
        console.error('❌ Unexpected error fetching mention details:', error);
        toast.error('Failed to load mention details');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleApprove = async (mentionId: string) => {
    console.log('✅ Approving mention:', mentionId);
    await updateMentionStatus(mentionId, 'approved');
    setModalOpen(false);
    // Refresh notifications to update the count
    fetchNotifications();
  };

  const handleReject = async (mentionId: string) => {
    console.log('❌ Rejecting mention:', mentionId);
    await updateMentionStatus(mentionId, 'rejected');
    setModalOpen(false);
    // Refresh notifications to update the count
    fetchNotifications();
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="relative">
            <Bell size={20} />
            {totalUnreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
              >
                {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <div className="p-2 border-b">
            <h4 className="font-semibold text-sm">Notifications</h4>
          </div>
          
          {/* Parent Access Requests for Athletes */}
          {profile?.role === 'athlete' && parentRequests.length > 0 && (
            <>
              {parentRequests.map((request) => (
                <DropdownMenuItem
                  key={request.id}
                  className="p-3 cursor-pointer hover:bg-muted/50 flex-col items-start"
                  onClick={(e) => e.preventDefault()}
                >
                  <div className="w-full">
                    <div className="flex items-start justify-between mb-1">
                      <h5 className="font-medium text-sm">Parent Access Request</h5>
                      <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {request.parent?.name || 'A parent'} wants to access your profile
                    </p>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        className="h-6 text-xs bg-green-600 hover:bg-green-700"
                        onClick={() => handleParentRequestResponse(request.id, 'approved')}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-xs border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleParentRequestResponse(request.id, 'rejected')}
                      >
                        Decline
                      </Button>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
              {notifications.length > 0 && <DropdownMenuSeparator />}
            </>
          )}

          {/* Existing notifications */}
          {notifications.length === 0 && parentRequests.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className="p-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h5 className="font-medium text-sm">{notification.title}</h5>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {notification.message}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {formatTimeAgo(notification.created_at)}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <MentionApprovalModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mention={selectedMention}
        article={selectedArticle}
        onApprove={handleApprove}
        onReject={handleReject}
        loading={loading}
      />

      <VideoMentionModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        mention={selectedVideoMention}
        video={selectedVideo}
        loading={loading}
      />
    </>
  );
};

export default NotificationDropdown;